import React, { useState, useEffect, useCallback } from 'react';
import { Box, useTheme, Typography, Skeleton } from '@mui/material';
import DashboardLayout from './DashboardLayout';
import { 
    getExecutiveData, 
    getEngagementData, 
    getMatchingData, 
    getMonetizationData, 
    getRetentionData 
} from './services/api';
import { tokens } from "../../theme";

// Import Tabs
import ExecutiveTab from './tabs/ExecutiveTab';
import EngagementTab from './tabs/EngagementTab';
import MatchingTab from './tabs/MatchingTab';
import MonetizationTab from './tabs/MonetizationTab';
import RetentionTab from './tabs/RetentionTab';
import ExportEmailsDialog from './components/ExportEmailsDialog';

const LoadingSkeleton = () => (
    <Box>
        <Box display="flex" gap={2} mb={2}>
            <Skeleton variant="rectangular" width={200} height={100} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" width={200} height={100} sx={{ borderRadius: 2 }} />
            <Skeleton variant="rectangular" width={200} height={100} sx={{ borderRadius: 2 }} />
        </Box>
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 2, mb: 2 }} />
        <Box display="flex" gap={2}>
             <Skeleton variant="rectangular" width="48%" height={200} sx={{ borderRadius: 2 }} />
             <Skeleton variant="rectangular" width="48%" height={200} sx={{ borderRadius: 2 }} />
        </Box>
    </Box>
);

const Dashboard2 = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // State
    const [currentTab, setCurrentTab] = useState(0);
    const [timeRange, setTimeRange] = useState('30d');
    const [planFilter, setPlanFilter] = useState('all');
    
    // Progressive Loading State
    const [data, setData] = useState({
        executive: null,
        engagement: null,
        matching: null,
        monetization: null,
        retention: null
    });
    
    const [loading, setLoading] = useState({
        executive: true,
        engagement: true,
        matching: true,
        monetization: true,
        retention: true
    });

    const [exportDialogOpen, setExportDialogOpen] = useState(false);

    // Fetch Helper
    const fetchData = useCallback(() => {
        const controller = new AbortController();

        // Reset loading states when filters change
        setLoading({
            executive: true,
            engagement: true,
            matching: true,
            monetization: true,
            retention: true
        });

        const fetchSection = (fetchFn, key) => {
            fetchFn(timeRange, planFilter, controller.signal)
                .then(res => {
                    setData(prev => ({ ...prev, [key]: res }));
                    setLoading(prev => ({ ...prev, [key]: false }));
                })
                .catch(err => {
                    // Ignore cancel errors
                    if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
                    console.error(`${key} data failed`, err);
                    setLoading(prev => ({ ...prev, [key]: false }));
                });
        };

        fetchSection(getExecutiveData, 'executive');
        fetchSection(getEngagementData, 'engagement');
        fetchSection(getMatchingData, 'matching');
        fetchSection(getMonetizationData, 'monetization');
        fetchSection(getRetentionData, 'retention');

        return () => controller.abort();

    }, [timeRange, planFilter]);

    // Initial Fetch & Cancellation
    useEffect(() => {
        const abort = fetchData();
        return () => {
             if (abort) abort();
        };
    }, [fetchData]);

    // Handle Tab Content
    const renderContent = () => {
        const renderTab = (isLoading, tabData, Component) => {
            if (isLoading) return <LoadingSkeleton />;
            if (!tabData) return <Typography color="error">Error loading section data. Please try refreshing.</Typography>;
            return <Component data={tabData} />;
        };

        switch (currentTab) {
            case 0: return renderTab(loading.executive, data.executive, ExecutiveTab);
            case 1: return renderTab(loading.engagement, data.engagement, EngagementTab);
            case 2: return renderTab(loading.matching, data.matching, MatchingTab);
            case 3: return renderTab(loading.monetization, data.monetization, MonetizationTab);
            case 4: return renderTab(loading.retention, data.retention, RetentionTab);
            default: return renderTab(loading.executive, data.executive, ExecutiveTab);
        }
    };

    return (
        <>
            <DashboardLayout
                currentTab={currentTab}
                onTabChange={(e, val) => setCurrentTab(val)}
                timeRange={timeRange}
                onTimeRangeChange={setTimeRange}
                planFilter={planFilter}
                onPlanFilterChange={setPlanFilter}
                onExportClick={() => setExportDialogOpen(true)}
            >
                {renderContent()}
            </DashboardLayout>
            
            <ExportEmailsDialog 
                open={exportDialogOpen}
                onClose={() => setExportDialogOpen(false)}
                planFilter={planFilter}
            />
        </>
    );
};

export default Dashboard2;
